/**
 * Widget schemas for the widget tools.
 *
 * Widgets are interactive UI components that can be inserted into chat responses.
 * Each widget type has a corresponding schema that defines its data structure
 * and provides usage instructions to the AI model via Zod's `.describe()` method.
 *
 * @module widget-schemas
 */

import type { UnionToTuple } from 'type-fest';

import type { Meta } from '@elara/core/types';

import type { ToolRequest, ToolResponse, WidgetPart } from '#models/schemas/message-schemas.js';

import { keys } from 'lodash-es';
import { z } from 'zod';

import { FlightOptionSchema, SearchGoogleFlightsSchema } from './flight-schemas.js';
import { HotelDataSchema } from './hotel-schemas.js';
import { OccupancySchema, RoomsSchema } from './room-schemas.js';
import { PreferenceCategorySchema, PreferenceEntityRefSchema } from './shared-schemas.js';

// Security constants for array size limits
const MAX_ARRAY_SIZE = 50; // Maximum items in any array
const MAX_STRING_LENGTH = 10_000; // Maximum string length
const MAX_WIDGET_COUNT = 10; // Maximum widgets per content string

export const BaseWidgetSchema = z.object({
  /**
   * @deprecated Disabled at the tool boundary as of ELARA-629. The model can
   * no longer pass `dataRef` because `widget-tool.ts` calls
   * `inputSchema = schema.omit({ dataRef: true })` when registering widget
   * tools — so this field is invisible to the LLM. The schema retains it
   * here ONLY for backwards compatibility with already-persisted historical
   * tool calls in Firestore (the `outputSchema` still includes it, and the
   * `WidgetTransformProcessor` still resolves dataRefs at render time for
   * those legacy entries). Do not add new code paths that depend on this
   * field; pass `data` directly instead.
   */
  dataRef: z
    .string()
    .describe(
      'Value of the `@dataRef` returned by the tool response which results you want to display in this widget. Must be provided if `data` is not provided.',
    )
    .nullish(),
});

/**
 * Factory function to create a widget schema with standard fields and AI instructions.
 *
 * @param name - The unique widget type name (e.g., 'Map', 'FlightSearch')
 * @param schema - The Zod schema for the widget's data field
 * @param instructions - Usage instructions for the AI model (added via `.describe()`)
 * @returns A Zod object schema with type, dataRef, and data fields
 */

export function defineWidgetSchema<Name extends string, T extends z.ZodTypeAny>({
  name,
  schema,
  instructions,
}: {
  name: Name;
  schema: T;
  instructions: string;
}) {
  return BaseWidgetSchema.extend({ data: schema.optional().describe('Raw data to display in the widget.') }).describe(
    instructions,
  );
}

/**
 * Schema for identifying entities when searching for images.
 * At least one ID should be provided for image lookup.
 */
const MAX_ID_LENGTH = 40; // Maximum length of an external image service ID

export const ImageSearchIdsSchema = z
  .object({
    googlePlaceId: z.string().nullish(),
    tripadvisorLocationId: z.string().nullish(),
    hotelCode: z.string().nullish(),
    roomCode: z.string().nullish(),
  })
  .refine(
    (data) => {
      // At least one ID must be provided (non-null, non-undefined, non-empty)
      return !!data.googlePlaceId || !!data.tripadvisorLocationId || !!data.hotelCode;
    },
    { message: 'At least one ID (googlePlaceId, tripadvisorLocationId, or hotelCode) must be provided' },
  );

export type ImageSearchIds = z.infer<typeof ImageSearchIdsSchema>;

/** Base image schema used by ImageGallery, Recommendation, and other widgets. */
export const WidgetImageSchema = z.object({
  id: z
    .string()
    .optional()
    .describe(
      'Assign any unique identifier for the image (e.g., "st-helen-beach-1", "hotel-azimondous-london-pool-2")',
    ),
  url: z.string().url(),
  caption: z.string().optional(),
  attribution: z.string().optional(),
  alt: z.string().optional(),
});

export type WidgetImage = z.infer<typeof WidgetImageSchema> & { id: string };
export type WidgetImageIn = z.input<typeof WidgetImageSchema>;

/** Data schema for the ImageGallery widget. */
export const ImageGalleryWidgetDataSchema = z.object({
  images: z.array(WidgetImageSchema),
  maxImages: z.number().describe('Max images displayed in card').optional(),
});

/** ImageGallery widget - displays a gallery of images with optional captions. */
export const ImageGalleryWidgetSchema = defineWidgetSchema({
  name: 'ImageGallery',
  schema: ImageGalleryWidgetDataSchema,
  instructions:
    'SHOWS images to the user when you ALREADY HAVE the URLs. ' +
    'REQUIRED: data.images array with objects containing id and url. ' +
    'Use showImagesByIdsWidget instead if you only have entity IDs (googlePlaceId, hotelCode). ' +
    'Example: { "data": { "images": [{ "id": "st-helen-beach-1", "url": "https://example.com/photo.jpg" }] } }',
});

/**
 * Schema for a question to ask the user.
 * All questions support multi-select and write-in answers by default.
 */
export const UserQuestionTypeSchema = z
  .enum(['single', 'multi', 'text'])
  .describe('The type of question to ask the user. Single: one choice, Multi: multiple choices, Text: free-form text.');
export const UserQuestionSchema = z.object({
  question: z
    .string()
    .describe(
      'The question to ask the user. DO NOT list choices in the question, use the choices field instead. Include specific information in the question. Bad question: "Are these dates okay?". Good question: "Is January 15th, 2026 okay for your trip?"',
    ),
  type: UserQuestionTypeSchema,
  choices: z
    .array(
      z
        .string()
        .describe(
          'The choices to display to the user in addition to a free-form answer. If no choices are provided, the user will only be able to provide a free-form answer. User will always be able to provide multiple choices.',
        ),
    )
    .optional(),
});
export type UserQuestionType = z.infer<typeof UserQuestionTypeSchema>;
export type UserQuestion = z.infer<typeof UserQuestionSchema>;
export type UserQuestionIn = z.input<typeof UserQuestionSchema>;
export const AskUserWidgetDataSchema = z.object({ questions: z.array(UserQuestionSchema) });
export const AskUserWidgetSchema = defineWidgetSchema({
  name: 'AskUser',
  schema: AskUserWidgetDataSchema,
  instructions:
    'Ask the user one or more questions, and pause the conversation until the user responds. Use choices to allow user to quickly select an answer. User will always be able to provide a custom answer. Hint: use multiple choices when questions has words "or" or enumerates choices.',
});
export type AskUserWidget = z.infer<typeof AskUserWidgetSchema>;
export type AskUserWidgetIn = z.input<typeof AskUserWidgetSchema>;

export const PreferenceProposalDecisionSchema = z.enum(['pending', 'yes', 'no']);
export const PreferenceProposalChoiceSchema = PreferenceProposalDecisionSchema.extract(['yes', 'no']);
export type PreferenceProposalChoice = z.infer<typeof PreferenceProposalChoiceSchema>;

export const PreferenceProposalStatusSchema = z.enum(['duplicate', 'declined', 'pending_exists', 'unknown_traveller', 'missing_target', 'ambiguous']);

export const PreferenceProposalWidgetDataSchema = z.looseObject({
  proposalId: z.string().describe('Stable identifier used when the client confirms or declines this proposal.'),
  category: PreferenceCategorySchema.describe('Type of preference this proposal would store.'),
  statement: z.string().describe('The durable fact the client is being asked to save.'),
  traveller: z.string().optional().describe('Named additional traveller this fact concerns, when applicable.'),
  avoid: z.literal(true).optional().describe('Do not recommend this entity. Always paired with entityRef.'),
  entityRef: PreferenceEntityRefSchema.optional().describe('Verified catalogue hotel/destination ID or Google-backed place record ID.'),
  generic: z.literal(true).optional().describe('A brand, chain, style, cuisine or other rule that names no single place record.'),
  decision: PreferenceProposalDecisionSchema.describe('Whether the client has confirmed, declined, or not yet decided.'),
}).refine((proposal) => !proposal.avoid || Boolean(proposal.entityRef), {
  message: 'An avoided entry requires an entity reference.',
  path: ['entityRef'],
});
export const PreferenceProposalWidgetSchema = defineWidgetSchema({
  name: 'PreferenceProposal',
  schema: PreferenceProposalWidgetDataSchema,
  instructions: 'Asks the client whether Elara should remember a durable fact for later trips.',
});

export const MapPointReferenceTypeSchema = z.enum(['place', 'hotel', 'destination', 'room']);
export type MapPointReferenceType = z.infer<typeof MapPointReferenceTypeSchema>;

/** Schema for a point on the map. */
export const MapPointSchema = z.object({
  id: z
    .string()
    .optional()
    .describe(
      'Pick a unique identifier for the point on the map (e.g., "hotel-azimondous-london", "restaurant-prestige-paris")',
    ),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  address: z.string().optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  referenceType: MapPointReferenceTypeSchema.optional().describe(
    'When set with referenceId, the widget loads the entity name and coordinates from Firestore and the marker click opens the entity guide.',
  ),
  referenceId: z
    .string()
    .optional()
    .describe(
      'Firestore document id of the place/hotel/destination/room to render. Must be paired with referenceType. Rooms also require a current trip context.',
    ),
});

export type MapPoint = z.infer<typeof MapPointSchema>;
export type MapPointIn = z.input<typeof MapPointSchema>;

/** Data schema for the Map widget. */
export const MapWidgetDataSchema = z.object({
  points: z.array(MapPointSchema).optional().default([]),
  center: z.object({ latitude: z.number().optional(), longitude: z.number().optional() }).optional(),
  zoom: z.number().int().min(1).max(20).optional(),
});

/** Map widget - shows locations on an interactive map. */
export const MapWidgetSchema = defineWidgetSchema({
  name: 'Map',
  schema: MapWidgetDataSchema,
  instructions:
    'SHOWS a map view to the user with one or more locations marked on it. ' +
    'Each point must EITHER provide explicit latitude+longitude (or address), OR a referenceType + referenceId pair. ' +
    'Prefer the reference form for known entities (place, hotel, destination, room) -- it loads the canonical name and coordinates ' +
    'from Firestore and lets the user open the guide on click. Room references are scoped to the current trip and will be skipped if no trip is open. ' +
    'Example (explicit): { "data": { "points": [{ "id": "paris-1", "latitude": 48.8566, "longitude": 2.3522, "label": "Paris" }] } }. ' +
    'Example (reference): { "data": { "points": [{ "id": "hotel-azim", "referenceType": "hotel", "referenceId": "00PdbyppWNxrpoHgFVnO" }] } }. ' +
    'The tool result returns a `marker`; write it on its own line where the map belongs in your reply — the map renders exactly there and the marker itself is never shown.',
});

export const RecommendationTypeSchema = z.enum(['hotel', 'destination', 'room', 'flight', 'place']);
export type RecommendationType = z.infer<typeof RecommendationTypeSchema>;

export const WIDGET_MARKER_OPEN = '[[widget:';
export const MAP_WIDGET_MARKER_ID = 'map';
export const WIDGET_MARKER_PATTERN = /\[\[widget:([^\]\s]+)\]\]/u;
export function formatWidgetMarker(markerId: string, nonce: string): string {
  return `${WIDGET_MARKER_OPEN}${markerId}-${nonce}]]`;
}
export const MARKED_WIDGET_TYPES: ReadonlySet<WidgetSchemaName> = new Set<WidgetSchemaName>([
  'showRecommendationWidget',
  'showMapWidget',
]);
export function getWidgetMarkerId(type: WidgetSchemaName, data: unknown): string | undefined {
  if (type === 'showMapWidget') return MAP_WIDGET_MARKER_ID;
  if (type !== 'showRecommendationWidget') return;
  if (typeof data !== 'object' || data === null) return;
  // A carousel names its entities in `referenceIds` and need not repeat the first one in
  // `referenceId`, so fall through to it — otherwise a multi-entity call mints no marker and its
  // card is stranded at the end of the reply rather than sitting where the model put it.
  const { referenceId, referenceIds } = data as { referenceId?: unknown; referenceIds?: unknown };
  if (typeof referenceId === 'string' && referenceId.length > 0) return referenceId;
  const first = Array.isArray(referenceIds) ? referenceIds[0] : undefined;
  return typeof first === 'string' && first.length > 0 ? first : undefined;
}

/** How many entities one recommendation widget will page through. A card row, not a gallery. */
export const MAX_RECOMMENDATION_IDS = 8;

/**
 * The recommendation card's shape, before the rule below. The model-facing variant has to branch off
 * HERE rather than from the exported schema: `.omit()` is a `ZodObject` method and `.refine()` returns
 * an effect, so the two variants each apply the rule to their own object.
 */
/**
 * A hotel's price line for its card, set by the SERVER — never by the model — from the search that
 * returned the hotel: the lead rate per night for one room, as the concierge was given it. The
 * widget tool lifts it from the turn's `fetchCuratedHotels` output by `_id`, so a card can only show
 * the same figure the prose may quote, and a model that misremembered a price cannot put it on a
 * card. Carried on the widget part today; rendering it is a later traveler-facing PR.
 */
const RecommendationFigureSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  /** True when the figure includes taxes and fees; false when that could not be confirmed. */
  taxesIncluded: z.boolean().optional(),
  /** The hotel's own published figure, which the Elara team would book — approximate, not a quote. */
  elaraTeamBooksDirect: z.boolean().optional(),
});
/**
 * No figure at all: the hotel has no booking-system id and nothing from the hotel online, so the
 * Elara team books it by asking. A card would say "Rates on request" where the price line goes.
 */
const RecommendationOnRequestSchema = z.object({ onRequest: z.literal(true) });
export const RecommendationPriceSchema = z.union([RecommendationFigureSchema, RecommendationOnRequestSchema]);
export type RecommendationPrice = z.infer<typeof RecommendationPriceSchema>;
export type RecommendationFigure = z.infer<typeof RecommendationFigureSchema>;

/**
 * What the curator said about a hotel for THIS trip, set by the SERVER from the search that
 * presented it — never by the model. Carried on the widget part for a card or the hotel guide to
 * show; nothing renders it yet (a later traveler-facing PR).
 */
export const RecommendationCurationSchema = z.object({
  /** One or two client-facing sentences: what this hotel gives them that the others do not. */
  reason: z.string(),
});
export type RecommendationCuration = z.infer<typeof RecommendationCurationSchema>;

const RecommendationWidgetDataObject = z.object({
  type: RecommendationTypeSchema,
  referenceId: z
    .string()
    .optional()
    .describe('The entity `_id` from the tool result that returned it — NEVER ids.global or ids.sabre.'),
  referenceIds: z
    .array(z.string())
    .max(MAX_RECOMMENDATION_IDS)
    .optional()
    .describe(
      `Several entity \`_id\`s of the SAME type, shown as one carousel (max ${MAX_RECOMMENDATION_IDS}). ` +
        'Prefer ONE call with every id over one call per entity. Use `referenceId` for a single entity.',
    ),
  /**
   * @deprecated Disabled at the tool boundary as of ELARA-1053 — the model
   * writes its prose as regular text parts instead, so new widgets never carry
   * a `body`. The field stays here ONLY so the widget parts already persisted
   * in Firestore keep parsing and rendering exactly as they did; both clients
   * still render it when present. Do not reintroduce it to the model-facing
   * schema ({@link RecommendationWidgetInputDataSchema} omits it).
   */
  body: z.string().optional().describe('LEGACY, never emit: prose carried by widgets persisted before ELARA-1053.'),
  /** SERVER-SET per entity `_id`; omitted from the model-facing schema like `body`. */
  prices: z.record(z.string(), RecommendationPriceSchema).optional(),
  /** SERVER-SET per entity `_id`; omitted from the model-facing schema like `body`. */
  curation: z.record(z.string(), RecommendationCurationSchema).optional(),
});

// `referenceId` became optional so a multi-entity widget need not repeat its first id, but a widget
// naming no entity at all has nothing to render — that is a malformed call, not an empty state.
const REQUIRE_AN_ENTITY = { message: 'Provide `referenceId` or a non-empty `referenceIds`', path: ['referenceId'] };

function namesAnEntity(data: { referenceId?: string; referenceIds?: string[] }): boolean {
  return !!data.referenceId || !!data.referenceIds?.length;
}

/** Data schema for the Recommendation widget. */
export const RecommendationWidgetDataSchema = RecommendationWidgetDataObject.refine(namesAnEntity, REQUIRE_AN_ENTITY);

/**
 * Model-facing variant of {@link RecommendationWidgetDataSchema} — the same card
 * without the deprecated `body`. `createWidgetTool` builds the tool's
 * `inputSchema` from this, so the model cannot pass `body` at all.
 */
export const RecommendationWidgetInputDataSchema = RecommendationWidgetDataObject.omit({
  body: true,
  prices: true,
  curation: true,
}).refine(namesAnEntity, REQUIRE_AN_ENTITY);

const RECOMMENDATION_WIDGET_INSTRUCTIONS =
  'SHOWS a card to the user for a destination, hotel, flight, room, place, or activity: its name, photos, key facts, and a shortlist button. ' +
  'The card carries NO prose of its own. The tool result returns a `marker`; write that marker on its own line where the card belongs, ' +
  'then what you have to say about the entity as normal text directly under it. The card renders exactly where its marker sits and the marker ' +
  'itself is never shown; a card without its marker is stranded at the end of the reply. That text never repeats the entity name, which the card already displays. ' +
  'REQUIRED: data.type, plus data.referenceId for ONE entity or data.referenceIds for several of the SAME type. ' +
  'Pass every entity of one type in a SINGLE call — several ids become one carousel under one marker, whereas one call each stacks separate cards down the reply. ' +
  'Example (one): call { "data": { "type": "hotel", "referenceId": "00PdbyppWNxrpoHgFVnO" } }, then write the returned marker (like "[[widget:00PdbyppWNxrpoHgFVnO-k3f9q2]]") on its own line, ' +
  'then "**Steps from the Louvre.** **$410/night** lowest-available. **Tradeoff:** compact rooms on lower floors." ' +
  'Example (several): call { "data": { "type": "hotel", "referenceIds": ["00PdbyppWNxrpoHgFVnO", "11ARCj8ZLDsaQml2knrT"] } }, then its marker, then how the two compare.';

/** Recommendation widget - displays a recommendation card with details. */
export const RecommendationWidgetSchema = defineWidgetSchema({
  name: 'Recommendation',
  schema: RecommendationWidgetDataSchema,
  instructions: RECOMMENDATION_WIDGET_INSTRUCTIONS,
});

/** Model-facing Recommendation widget schema — {@link RecommendationWidgetSchema} without `body`. */
export const RecommendationWidgetInputSchema = defineWidgetSchema({
  name: 'Recommendation',
  schema: RecommendationWidgetInputDataSchema,
  instructions: RECOMMENDATION_WIDGET_INSTRUCTIONS,
});

/** Data schema for the ImageSearch widget. */
export const ImageSearchWidgetDataSchema = z.object({
  ids: ImageSearchIdsSchema,
  limit: z.number().max(20).default(6).optional(),
});

/** ImageSearch widget - fetches and displays photos for hotels or places by ID. */
export const ImageSearchWidgetSchema = defineWidgetSchema({
  name: 'ImageSearchByIds',
  schema: ImageSearchWidgetDataSchema,
  instructions:
    'SHOWS photos to the user for a hotel or place BY ITS ID when the IDs are already known. NOT for general searches. ' +
    'REQUIRED: data.ids with at least one of: googlePlaceId, tripadvisorLocationId, hotelCode. ' +
    'Get these IDs from hotel/place data you already have. ' +
    'Example: { "data": { "ids": { "googlePlaceId": "ChIJ..." } } }',
});

/** Data schema for the FlightSearch widget. */
export const FlightSearchWidgetDataSchema = z.object({
  search: SearchGoogleFlightsSchema.optional(),
  // Keep result items structurally permissive for provider tool-schema conversion.
  // Full fidelity is still preserved when data is resolved from dataRef at runtime.
  results: z.array(FlightOptionSchema.partial()),
  total: z.number(),
  source: z.string(),
});

/** FlightSearch widget - displays flight search results from the fetchFlightData tool. */
export const FlightSearchWidgetSchema = defineWidgetSchema({
  name: 'FlightSearch',
  schema: FlightSearchWidgetDataSchema,
  instructions:
    'SHOWS flight search results to the user. Use this after calling fetchFlightData. Pass the flight results from the fetchFlightData tool response into `data`.',
});

/** Schema for room search request parameters. */
export const RoomSearchRequestSchema = z.object({
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  hotelId: z.string(),
  occupancy: OccupancySchema.describe('Single occupancy used for this room search'),
});

export type RoomSearchRequest = z.infer<typeof RoomSearchRequestSchema>;

/** Schema for room search response data. */
export const RoomSearchResponseSchema = z.object({
  request: RoomSearchRequestSchema,
  hotel: HotelDataSchema,
  rooms: z.array(RoomsSchema).max(MAX_ARRAY_SIZE).optional().default([]),
  warnings: z.string().max(MAX_STRING_LENGTH).optional(),
});

/** Data schema for the RoomSearch widget. */
export const RoomSearchWidgetDataSchema = RoomSearchResponseSchema;

/** RoomSearch widget - displays hotel room availability and pricing with pricing. */
export const RoomSearchWidgetSchema = defineWidgetSchema({
  name: 'RoomSearch',
  schema: RoomSearchWidgetDataSchema,
  instructions:
    'SHOWS hotel room availability and pricing to the user. Use this after calling fetchRoomAvailability. ' +
    'Pass the room results from the fetchRoomAvailability tool response into `data`.',
});

/** Registry mapping widget type names to their corresponding schemas. */
export const WidgetSchemas = {
  showImageGalleryWidget: ImageGalleryWidgetSchema,
  showMapWidget: MapWidgetSchema,
  showRecommendationWidget: RecommendationWidgetSchema,
  showImagesByIdsWidget: ImageSearchWidgetSchema,
  showFlightResultsWidget: FlightSearchWidgetSchema,
  showRoomResultsWidget: RoomSearchWidgetSchema,
  showPreferenceProposalWidget: PreferenceProposalWidgetSchema,
} as const satisfies Record<`show${string}Widget`, typeof BaseWidgetSchema>;

/**
 * Model-facing widget schemas: what the LLM is allowed to send.
 *
 * Identical to {@link WidgetSchemas} except where a field has been deprecated —
 * the storage/validation schema keeps it so historical widget parts still parse
 * and render, while the model-facing schema omits it. `createWidgetTool` builds
 * each tool's `inputSchema` from this map and its `outputSchema` from
 * {@link WidgetSchemas}.
 */
export const WidgetInputSchemas = {
  ...WidgetSchemas,
  showRecommendationWidget: RecommendationWidgetInputSchema,
} as const satisfies Record<keyof typeof WidgetSchemas, typeof BaseWidgetSchema>;

/**
 * Legacy widget names mapping for backwards compatibility with existing chat history.
 * Tool calls stored in Firestore contain the old names, so we need to map them.
 */
export const LEGACY_WIDGET_NAMES: Record<string, WidgetSchemaName> = {
  ImageGalleryWidget: 'showImageGalleryWidget',
  MapWidget: 'showMapWidget',
  RecommendationWidget: 'showRecommendationWidget',
  ImageSearchByIdsWidget: 'showImagesByIdsWidget',
  FlightSearchWidget: 'showFlightResultsWidget',
  RoomSearchWidget: 'showRoomResultsWidget',
};

export type WidgetSchemas = typeof WidgetSchemas;
export type WidgetSchemaName = keyof WidgetSchemas;
export const WidgetsMeta: Partial<Record<WidgetSchemaName, Meta>> = {};

export function getWidgetMeta(name: WidgetSchemaName): Meta | undefined {
  return name in WidgetsMeta ? WidgetsMeta[name] : undefined;
}

const WidgetTypes = keys(WidgetSchemas) as UnionToTuple<WidgetSchemaName>;
export const WidgetTypeSchema = z.enum(WidgetTypes);

/**
 * Discriminated union of all widget schemas.
 * Used for strict validation where the widget type must match one of the known types.
 */
export const WidgetSchema = z.union([
  WidgetSchemas.showImageGalleryWidget,
  WidgetSchemas.showMapWidget,
  WidgetSchemas.showRecommendationWidget,
  WidgetSchemas.showImagesByIdsWidget,
  WidgetSchemas.showFlightResultsWidget,
  WidgetSchemas.showRoomResultsWidget,
  WidgetSchemas.showPreferenceProposalWidget,
]);

/** Widget with data field explicitly undefined or null (before data resolution). */
export type UnresolvedWidget = z.output<typeof WidgetSchema> & { data?: undefined | null };

/** Fully resolved widget with validated data. */
export type Widget = z.infer<typeof WidgetSchema>;

/** Union of all valid widget type names. */
export type WidgetType = keyof typeof WidgetSchemas;

/** Extracts the data type for a specific widget type. */
export type WidgetData<WT extends WidgetType> = NonNullable<z.output<WidgetSchemas[WT]>['data']>;

/**
 * Type-safe validation function for widget data.
 *
 * @param widgetType - The widget type to validate against
 * @param data - The data to validate
 * @returns A Zod SafeParseReturnType with the validated data or error
 */
export function validateWidgetData<const Data, const WT extends WidgetType>(widgetType: WT, data: Data) {
  const schema = WidgetSchemas[widgetType]?.shape.data;
  if (!schema) {
    return {
      success: false as const,
      error: new z.ZodError([{ code: 'custom', message: `Unknown widget type: ${widgetType}`, path: [] }]),
    };
  }
  return schema.safeParse(data);
}

// ============================================================================
// Full Widget Types (includes type, data, dataRef)
// ============================================================================

/** Full ImageGallery widget type with all fields. */
export type ImageGalleryWidget = z.infer<typeof ImageGalleryWidgetSchema>;

/** Full Map widget type with all fields. */
export type MapWidget = z.infer<typeof MapWidgetSchema>;

/** Full Recommendation widget type with all fields. */
export type RecommendationWidget = z.infer<typeof RecommendationWidgetSchema>;

/** Full ImageSearch widget type with all fields. */
export type ImageSearchWidget = z.infer<typeof ImageSearchWidgetSchema>;

/** Full FlightSearch widget type with all fields. */
export type FlightSearchWidget = z.infer<typeof FlightSearchWidgetSchema>;

/** Full RoomSearch widget type with all fields. */
export type RoomSearchWidget = z.infer<typeof RoomSearchWidgetSchema>;

/** Data type for ImageGallery widget. */
export type ImageGalleryWidgetData = WidgetData<'showImageGalleryWidget'>;

/** Data type for Map widget. */
export type MapWidgetData = WidgetData<'showMapWidget'>;

/** Data type for Recommendation widget. */
export type RecommendationWidgetData = WidgetData<'showRecommendationWidget'>;

/** Data type for ImageSearchByIds widget. */
export type ImageSearchWidgetData = WidgetData<'showImagesByIdsWidget'>;

/** Data type for FlightSearch widget. */
export type FlightSearchWidgetData = WidgetData<'showFlightResultsWidget'>;

/** Data type for RoomSearch widget. */
export type RoomSearchWidgetData = WidgetData<'showRoomResultsWidget'>;

export function createWidgetPart<WT extends WidgetType>(type: WT, payload: z.input<WidgetSchemas[WT]>) {
  return { widget: { type, payload } } satisfies WidgetPart;
}

/**
 * Check if a tool response is from a widget tool.
 * Supports both new and legacy widget names for backwards compatibility.
 */
export function isWidgetToolPayload(toolPayload: ToolResponse | ToolRequest): boolean {
  return toolPayload.name in WidgetSchemas || toolPayload.name in LEGACY_WIDGET_NAMES;
}

/**
 * Normalize a widget name to the current schema name.
 * Handles backwards compatibility with legacy names stored in Firestore.
 */
export function normalizeWidgetName(name: string): WidgetSchemaName | undefined {
  if (name in WidgetSchemas) {
    return name as WidgetSchemaName;
  }
  if (name in LEGACY_WIDGET_NAMES) {
    return LEGACY_WIDGET_NAMES[name];
  }
  return undefined;
}

export const WIDGET_SECURITY_LIMITS = { MAX_ARRAY_SIZE, MAX_STRING_LENGTH, MAX_WIDGET_COUNT } as const;
